import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Clock, Film, Loader, Plus, Play, Send, X } from 'lucide-react';
import { peakMomentScenesAPI } from '../services/api';

const colors = {
  white: '#ffffff',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray800: '#1F2937',
  gray900: '#111827',
  orange: '#F97316',
  purple: '#8B5CF6'
};

const inputStyle = {
  width: '100%',
  padding: '11px 12px',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  background: 'rgba(255,255,255,0.04)',
  color: '#fff',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box'
};

const VideoModal = ({ reaction, onClose }) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const params = new URLSearchParams({
    autoplay: '1',
    rel: '0',
    origin,
    start: String(Math.max(0, Number(reaction?.timestampSeconds) || 0))
  }).toString();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!reaction?.videoId) return null;

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <button onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ position: 'absolute', top: 16, right: 20, width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.12)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <X size={18} />
      </button>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '90vw', maxWidth: 960, aspectRatio: '16/9', borderRadius: 12, overflow: 'hidden', background: '#000' }}>
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${reaction.videoId}?${params}`}
          title={reaction.title || 'Scene reaction'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        />
      </div>
    </div>,
    document.body
  );
};

const PeakMomentScenes = ({ isMobile = false, onToast }) => {
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSceneId, setSelectedSceneId] = useState(null);
  const [activeReaction, setActiveReaction] = useState(null);
  const [savingScene, setSavingScene] = useState(false);
  const [savingReaction, setSavingReaction] = useState(false);
  const [sceneForm, setSceneForm] = useState({
    contentTitle: '',
    contentType: 'tv-show',
    sceneTitle: '',
    sceneTimestamp: '',
    sceneDescription: '',
    posterUrl: ''
  });
  const [reactionForm, setReactionForm] = useState({
    title: '',
    youtubeUrl: '',
    timestampSeconds: '',
    creatorName: '',
    channelName: ''
  });

  const selectedScene = useMemo(
    () => scenes.find(scene => scene.id === selectedSceneId) || scenes[0],
    [scenes, selectedSceneId]
  );

  const loadScenes = async () => {
    try {
      setLoading(true);
      const response = await peakMomentScenesAPI.getScenes({ limit: 50 });
      setScenes(response.data || []);
      if (!selectedSceneId && response.data?.[0]?.id) setSelectedSceneId(response.data[0].id);
    } catch (error) {
      onToast?.({ type: 'error', message: 'Failed to load scene moments' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadScenes();
  }, []);

  const createScene = async () => {
    if (!sceneForm.contentTitle || !sceneForm.sceneTitle) return;
    try {
      setSavingScene(true);
      const response = await peakMomentScenesAPI.createScene(sceneForm);
      await loadScenes();
      setSelectedSceneId(response.data.id);
      setSceneForm({ contentTitle: '', contentType: 'tv-show', sceneTitle: '', sceneTimestamp: '', sceneDescription: '', posterUrl: '' });
      onToast?.({ type: 'success', message: 'Scene saved' });
    } catch (error) {
      onToast?.({ type: 'error', message: error.response?.data?.error || 'Could not save scene' });
    } finally {
      setSavingScene(false);
    }
  };

  const addReaction = async () => {
    if (!selectedScene?.id || !reactionForm.title || !reactionForm.youtubeUrl) return;
    try {
      setSavingReaction(true);
      const response = await peakMomentScenesAPI.addReaction(selectedScene.id, reactionForm);
      setScenes(prev => prev.map(scene => scene.id === selectedScene.id ? response.data : scene));
      setReactionForm({ title: '', youtubeUrl: '', timestampSeconds: '', creatorName: '', channelName: '' });
      onToast?.({ type: 'success', message: 'Reaction added to scene' });
    } catch (error) {
      onToast?.({ type: 'error', message: error.response?.data?.error || 'Could not add reaction' });
    } finally {
      setSavingReaction(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', color: colors.gray400, gap: 10 }}>
        <Loader size={22} style={{ animation: 'spin 1s linear infinite' }} />
        Loading scenes...
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '340px 1fr', gap: 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ padding: 16, borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Plus size={16} color={colors.orange} />
            <h3 style={{ margin: 0, fontSize: 16 }}>Create Scene</h3>
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            <input style={inputStyle} placeholder="Show or movie title" value={sceneForm.contentTitle} onChange={(e) => setSceneForm(prev => ({ ...prev, contentTitle: e.target.value }))} />
            <input style={inputStyle} placeholder="Memorable scene" value={sceneForm.sceneTitle} onChange={(e) => setSceneForm(prev => ({ ...prev, sceneTitle: e.target.value }))} />
            <input style={inputStyle} placeholder="Scene time, episode, or context" value={sceneForm.sceneTimestamp} onChange={(e) => setSceneForm(prev => ({ ...prev, sceneTimestamp: e.target.value }))} />
            <textarea style={{ ...inputStyle, minHeight: 74, resize: 'vertical' }} placeholder="Why this scene matters" value={sceneForm.sceneDescription} onChange={(e) => setSceneForm(prev => ({ ...prev, sceneDescription: e.target.value }))} />
            <button onClick={createScene} disabled={savingScene || !sceneForm.contentTitle || !sceneForm.sceneTitle} style={{ padding: 12, border: 'none', borderRadius: 10, background: 'linear-gradient(135deg, #F97316, #EA580C)', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: (!sceneForm.contentTitle || !sceneForm.sceneTitle) ? 0.55 : 1 }}>
              {savingScene ? 'Saving...' : 'Save Scene'}
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gap: 10 }}>
          {scenes.map(scene => (
            <button key={scene.id} onClick={() => setSelectedSceneId(scene.id)} style={{ padding: 14, border: selectedScene?.id === scene.id ? `1px solid ${colors.orange}` : '1px solid rgba(255,255,255,0.08)', borderRadius: 12, background: selectedScene?.id === scene.id ? 'rgba(249,115,22,0.12)' : 'rgba(255,255,255,0.035)', color: '#fff', textAlign: 'left', cursor: 'pointer' }}>
              <div style={{ fontSize: 12, color: colors.gray400, marginBottom: 4 }}>{scene.contentTitle}</div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{scene.sceneTitle}</div>
              <div style={{ fontSize: 12, color: colors.gray500, marginTop: 6 }}>{scene.reactionCount} creator moment{scene.reactionCount === 1 ? '' : 's'}</div>
            </button>
          ))}
        </div>
      </div>

      <div style={{ minWidth: 0 }}>
        {selectedScene ? (
          <>
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: colors.orange, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                <Film size={16} />
                {selectedScene.contentTitle}
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: isMobile ? 24 : 32 }}>{selectedScene.sceneTitle}</h2>
              <p style={{ margin: 0, color: colors.gray400, lineHeight: 1.5 }}>{selectedScene.sceneDescription || 'Collect the best creator reactions to this scene.'}</p>
              {selectedScene.sceneTimestamp && <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, color: colors.gray400, fontSize: 13 }}><Clock size={14} />{selectedScene.sceneTimestamp}</div>}
            </div>

            <div style={{ padding: 16, borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Add Creator Peak Moment</h3>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1.6fr 0.7fr', gap: 10, marginBottom: 10 }}>
                <input style={inputStyle} placeholder="Moment title" value={reactionForm.title} onChange={(e) => setReactionForm(prev => ({ ...prev, title: e.target.value }))} />
                <input style={inputStyle} placeholder="YouTube reaction URL" value={reactionForm.youtubeUrl} onChange={(e) => setReactionForm(prev => ({ ...prev, youtubeUrl: e.target.value }))} />
                <input style={inputStyle} placeholder="Start seconds" value={reactionForm.timestampSeconds} onChange={(e) => setReactionForm(prev => ({ ...prev, timestampSeconds: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr auto', gap: 10 }}>
                <input style={inputStyle} placeholder="Creator name" value={reactionForm.creatorName} onChange={(e) => setReactionForm(prev => ({ ...prev, creatorName: e.target.value }))} />
                <input style={inputStyle} placeholder="Channel name" value={reactionForm.channelName} onChange={(e) => setReactionForm(prev => ({ ...prev, channelName: e.target.value }))} />
                <button onClick={addReaction} disabled={savingReaction || !reactionForm.title || !reactionForm.youtubeUrl} style={{ padding: '0 18px', minHeight: 42, border: 'none', borderRadius: 10, background: colors.purple, color: '#fff', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: (!reactionForm.title || !reactionForm.youtubeUrl) ? 0.55 : 1 }}>
                  <Send size={15} />
                  {savingReaction ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
              {(selectedScene.reactions || []).map(reaction => (
                <button key={reaction._id || `${reaction.videoId}-${reaction.timestampSeconds}`} onClick={() => setActiveReaction(reaction)} style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, overflow: 'hidden', padding: 0, background: colors.gray900, color: '#fff', textAlign: 'left', cursor: 'pointer' }}>
                  <div style={{ position: 'relative', aspectRatio: '16/9', background: colors.gray800 }}>
                    <img src={reaction.thumbnail || `https://img.youtube.com/vi/${reaction.videoId}/mqdefault.jpg`} alt={reaction.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.18)' }}>
                      <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Play size={20} color="#111" fill="#111" style={{ marginLeft: 3 }} />
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 5 }}>{reaction.title}</div>
                    <div style={{ color: colors.gray400, fontSize: 12 }}>{reaction.creatorName || reaction.channelName || 'Creator reaction'} at {reaction.timestampFormatted}</div>
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div style={{ padding: '80px 20px', textAlign: 'center', color: colors.gray400 }}>Create the first show scene to start collecting creator reactions.</div>
        )}
      </div>

      {activeReaction && <VideoModal reaction={activeReaction} onClose={() => setActiveReaction(null)} />}
    </div>
  );
};

export default PeakMomentScenes;
